import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { shouldPolyfill as shouldPolyfillPluralRuleLocale } from '@formatjs/intl-pluralrules/should-polyfill.js';
import { shouldPolyfill as shouldPolyfillRelativeTimeFormatLocale } from '@formatjs/intl-relativetimeformat/should-polyfill.js';
import { usePostHog } from '@posthog/react';
import { appLocales } from '../../i18n';

async function intlRelativeTimeFormatPolyfill(locale: string) {
  const unsupportedLocale = shouldPolyfillRelativeTimeFormatLocale(locale);
  // This locale is supported
  if (!unsupportedLocale) {
    return;
  }
  // Load the polyfill 1st BEFORE loading data
  await import('@formatjs/intl-relativetimeformat/polyfill-force.js');
  switch (locale) {
    case 'en':
      await import('@formatjs/intl-relativetimeformat/locale-data/en');
      return;
    case 'es':
      await import('@formatjs/intl-relativetimeformat/locale-data/es');
      return;
    case 'fr':
      await import('@formatjs/intl-relativetimeformat/locale-data/fr');
      return;
    case 'nb':
      await import('@formatjs/intl-relativetimeformat/locale-data/nb');
      return;
    case 'ru':
      await import('@formatjs/intl-relativetimeformat/locale-data/ru');
      return;
    case 'de':
    default:
      await import('@formatjs/intl-relativetimeformat/locale-data/de');
  }
}

async function intlPluralRulesPolyfill(locale: (typeof appLocales)[number]) {
  const unsupportedLocale = shouldPolyfillPluralRuleLocale(locale);
  // This locale is supported
  if (!unsupportedLocale) {
    return;
  }
  // Load the polyfill 1st BEFORE loading data
  await import('@formatjs/intl-pluralrules/polyfill-force.js');
  switch (locale) {
    case 'en':
      await import('@formatjs/intl-pluralrules/locale-data/en');
      return;
    case 'es':
      await import('@formatjs/intl-pluralrules/locale-data/es');
      return;
    case 'fr':
      await import('@formatjs/intl-pluralrules/locale-data/fr');
      return;
    case 'nb':
      await import('@formatjs/intl-pluralrules/locale-data/nb');
      return;
    case 'ru':
      await import('@formatjs/intl-pluralrules/locale-data/ru');
      return;
    case 'de':
    default:
      await import('@formatjs/intl-pluralrules/locale-data/de');
  }
}

// https://fettblog.eu/typescript-array-includes/#option-2%3A-a-helper-with-type-assertions
function includes<T extends U, U>(coll: ReadonlyArray<T>, el: U): el is T {
  return coll.includes(el as T);
}

type ParsedLocale =
  | {
      isUnknown: true;
      detectedLocale: 'en';
    }
  | {
      isUnknown: false;
      detectedLocale: (typeof appLocales)[number];
    };

const useLocaleFromCookie = (): ParsedLocale => {
  const posthog = usePostHog();
  const languageCookie = document.cookie
    .split(';')
    .find((item) => item.trim().startsWith('.AspNetCore.Culture='));
  const match = languageCookie
    ? decodeURIComponent(languageCookie).match(/uic=([a-z]+)/)
    : undefined;
  const parsedLocale = match?.[1];
  const isUnknown = !parsedLocale || !includes(appLocales, parsedLocale);
  useEffect(() => {
    if (isUnknown) {
      posthog.capture('Unknown locale detected', {
        level: 'warning',
        extra: {
          parsedLocale: parsedLocale || 'undefined',
          aspNetCoreCultureCookie: languageCookie || 'not_found',
          supportedLocales: appLocales,
        },
      });
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnknown]);
  if (isUnknown) {
    return { isUnknown: true, detectedLocale: 'en' as const };
  }
  return { isUnknown: false, detectedLocale: parsedLocale };
};

export const useUILanguageState = () => {
  const parsedLocale = useLocaleFromCookie();
  const [shouldShowUnknownLocaleModal, setShouldShowUnknownLocaleModal] =
    useState(parsedLocale.isUnknown);
  const [locale, setLocale] = useState<ParsedLocale['detectedLocale'] | null>(
    () => {
      if (moment.locale() === parsedLocale.detectedLocale) {
        return parsedLocale.detectedLocale;
      }
      return null;
    }
  );
  const handleSetLocale = async (
    nextLocale: ParsedLocale['detectedLocale']
  ) => {
    if (import.meta.env.NODE_ENV !== 'test') {
      if (nextLocale !== 'en') {
        // en is support by moment by default
        await import(`./locales/${nextLocale}.ts`);
      }
      await intlPluralRulesPolyfill(nextLocale);
      await intlRelativeTimeFormatPolyfill(nextLocale);
    }
    moment.locale(nextLocale);
    moment.updateLocale(nextLocale, {
      week: {
        dow: 1,
      },
    });
    setLocale(nextLocale);
  };
  useEffect(() => {
    // External-system sync: lazy-loads locale data and configures moment/Intl
    // polyfills. setState here reflects async completion, not a render-derived
    // value, so the set-state-in-effect rule does not apply.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleSetLocale(parsedLocale.detectedLocale);
    setShouldShowUnknownLocaleModal(parsedLocale.isUnknown);
  }, [parsedLocale.detectedLocale, parsedLocale.isUnknown]);
  return {
    shouldShowUnknownLocaleModal,
    locale,
    setLocale: async (newLocale: (typeof appLocales)[number]) => {
      const origin = import.meta.env.TEST
        ? 'http://localhost:3000'
        : window.location.origin;
      const response = await fetch(
        `${origin}/api/Dashboards/my?language=${newLocale}`
      );
      if (!response.ok) {
        return;
      }
      await handleSetLocale(newLocale);
      setShouldShowUnknownLocaleModal(false);
    },
  };
};

export const UILanguageContext = React.createContext<
  | ({ locale: (typeof appLocales)[number] } & Pick<
      ReturnType<typeof useUILanguageState>,
      'setLocale' | 'shouldShowUnknownLocaleModal'
    >)
  | null
>(null);

export const UILanguageProvider = (props: { children: React.ReactNode }) => {
  const { locale, setLocale, shouldShowUnknownLocaleModal } =
    useUILanguageState();
  const value = useMemo(
    () => (locale ? { locale, setLocale, shouldShowUnknownLocaleModal } : null),
    [locale, setLocale, shouldShowUnknownLocaleModal]
  );
  useEffect(() => {
    if (value?.locale) {
      document.documentElement.lang = value.locale;
    }
  }, [value?.locale]);

  return locale ? (
    <UILanguageContext.Provider value={value}>
      {props.children}
    </UILanguageContext.Provider>
  ) : null;
};

export const useUILanguage = () => {
  const context = useContext(UILanguageContext);
  if (!context) {
    throw new Error('Please wrap your component with the UILanguageProvider');
  }
  return context;
};
